import {useCallback, useEffect, useRef, useState} from 'react';
import {logger} from '@/helpers/logger';

// Must mirror Peacock's internal getRowId key order.
const PEACOCK_ROW_ID_KEYS = ['id', 'Id', 'ID', '_id', 'uuid', 'key'];

// Minimum rows moved in one update to count as a header-checkbox gesture.
const HEADER_TOGGLE_MIN_ROWS = 2;

export const getRecordIdValue = (record) => {
  if (!record || typeof record !== 'object') return undefined;
  for (const key of PEACOCK_ROW_ID_KEYS) {
    if (record[key] != null) return record[key];
  }
  return undefined;
};

export const getRecordId = (record) => {
  const value = getRecordIdValue(record);
  return value != null ? String(value) : undefined;
};

const INITIAL_UI = {
  allMode: false,
  pageFullySelected: false,
  excludedCount: 0,
  selectedCount: 0
};

/**
 * Dataset-wide select-all model on top of the Peacock DataTable's page-scoped,
 * uncontrolled selection. Opt-in via `enabled`; when off the hook only mirrors
 * the selection into refs (legacy stateless behavior).
 *
 * Armed select-all spans all pages, unchecks become `excludeIds`, new pages
 * auto-check, and a header-uncheck of a fully selected page clears everything.
 * Download meta: `{isAllSelected, excludeIds}`.
 * Known limitations: docs/peacock-datatable-usage.md.
 */
export const usePeacockSelection = ({
  records = [],
  totalRecords = 0,
  onRowSelect,
  enabled = false,
  mode = 'banner'
}) => {
  const isAllSelectedRef = useRef(false);
  // Map<string id, original id value> — keeps the backend's id type.
  const excludeIdsRef = useRef(new Map());
  const selectedRowsRef = useRef([]);
  const prevReportedIdsRef = useRef(new Set());
  const tableRef = useRef(null);
  // Report expected from our own auto-check write; a matching report is a
  // baseline update, anything else is user intent.
  const expectedReportRef = useRef(null);
  // Peacock fires the updater callback during its own setState evaluation,
  // where synchronous table mutations are unreliable — resets are deferred
  // to the post-commit effect below.
  const pendingTableResetRef = useRef(false);

  const recordsRef = useRef(records);
  const totalRecordsRef = useRef(totalRecords);
  const onRowSelectRef = useRef(onRowSelect);
  const enabledRef = useRef(enabled);
  const modeRef = useRef(mode);
  recordsRef.current = records;
  totalRecordsRef.current = totalRecords;
  onRowSelectRef.current =
    typeof onRowSelect === 'function' ? onRowSelect : () => {};
  enabledRef.current = enabled;
  modeRef.current = mode;

  const [selectionUi, setSelectionUi] = useState(INITIAL_UI);

  const handleTableReady = useCallback((table) => {
    tableRef.current = table;
  }, []);

  const clearSelectionModel = useCallback(() => {
    isAllSelectedRef.current = false;
    excludeIdsRef.current = new Map();
    selectedRowsRef.current = [];
    prevReportedIdsRef.current = new Set();
    expectedReportRef.current = null;
    pendingTableResetRef.current = true;
    setSelectionUi(INITIAL_UI);
    onRowSelectRef.current([]);
  }, []);

  // Post-commit executor for deferred table resets.
  useEffect(() => {
    if (pendingTableResetRef.current) {
      pendingTableResetRef.current = false;
      tableRef.current?.resetRowSelection();
    }
  });

  const enterSelectAllMode = useCallback(() => {
    if (!enabledRef.current) return;
    isAllSelectedRef.current = true;
    excludeIdsRef.current = new Map();
    setSelectionUi({
      allMode: true,
      pageFullySelected: false,
      excludedCount: 0,
      selectedCount: selectedRowsRef.current.length
    });
  }, []);

  const handleRowSelectionUpdate = useCallback(
    (rows) => {
      const rowsArray = Array.isArray(rows) ? rows : [];
      selectedRowsRef.current = rowsArray;
      onRowSelectRef.current(rowsArray.map((row) => row.original || row));

      if (!enabledRef.current) return;

      const reportedIds = new Set(
        rowsArray
          .map((row) => row.id ?? getRecordId(row.original || row))
          .filter((id) => id != null)
          .map(String)
      );

      // Consume the expectation on every callback — a stale one must not
      // swallow a later user gesture.
      const expected = expectedReportRef.current;
      expectedReportRef.current = null;
      if (
        expected &&
        reportedIds.size === expected.size &&
        [...reportedIds].every((id) => expected.has(id))
      ) {
        prevReportedIdsRef.current = reportedIds;
        setSelectionUi((prev) => ({...prev, selectedCount: rowsArray.length}));
        return;
      }

      const prevReportedIds = prevReportedIdsRef.current;
      prevReportedIdsRef.current = reportedIds;

      const pageRecords = recordsRef.current || [];
      const pageIds = pageRecords.map(getRecordId).filter(Boolean);

      if (isAllSelectedRef.current) {
        // A page row is newly excluded only when this update removed it —
        // fresh pages awaiting auto-check are not exclusions.
        let removedByThisUpdate = 0;
        pageRecords.forEach((record) => {
          const id = getRecordId(record);
          if (!id) return;
          if (reportedIds.has(id)) {
            excludeIdsRef.current.delete(id);
          } else if (prevReportedIds.has(id)) {
            removedByThisUpdate += 1;
            excludeIdsRef.current.set(id, getRecordIdValue(record));
          }
        });

        // Header-uncheck (whole page emptied at once) → exit select-all.
        if (
          modeRef.current === 'header' &&
          removedByThisUpdate >= HEADER_TOGGLE_MIN_ROWS &&
          pageIds.length > 0 &&
          pageIds.every((id) => !reportedIds.has(id))
        ) {
          clearSelectionModel();
          return;
        }

        // Everything excluded → nothing is effectively selected anymore.
        if (
          totalRecordsRef.current > 0 &&
          excludeIdsRef.current.size >= totalRecordsRef.current
        ) {
          clearSelectionModel();
          return;
        }

        setSelectionUi({
          allMode: true,
          pageFullySelected: false,
          excludedCount: excludeIdsRef.current.size,
          selectedCount: rowsArray.length
        });
        return;
      }

      // Header-uncheck of a fully selected page clears everything, armed or
      // not (legacy parity); partial-page resets stay on the normal path.
      if (
        modeRef.current === 'header' &&
        pageIds.length >= HEADER_TOGGLE_MIN_ROWS &&
        pageIds.every((id) => prevReportedIds.has(id)) &&
        pageIds.every((id) => !reportedIds.has(id))
      ) {
        clearSelectionModel();
        return;
      }

      const pageFullySelected =
        pageIds.length > 0 && pageIds.every((id) => reportedIds.has(id));

      // Header-check: page jumps to fully selected with several rows added
      // in one update → arm dataset-wide select-all.
      if (modeRef.current === 'header' && pageFullySelected) {
        const wasFullySelected =
          pageIds.length > 0 && pageIds.every((id) => prevReportedIds.has(id));
        const addedByThisUpdate = pageIds.filter(
          (id) => reportedIds.has(id) && !prevReportedIds.has(id)
        ).length;
        if (!wasFullySelected && addedByThisUpdate >= HEADER_TOGGLE_MIN_ROWS) {
          enterSelectAllMode();
          return;
        }
      }

      setSelectionUi({
        allMode: false,
        pageFullySelected,
        excludedCount: 0,
        selectedCount: rowsArray.length
      });
    },
    [clearSelectionModel, enterSelectAllMode]
  );

  // A filter change redefines "all" — exit select-all; explicit selections
  // keep their pre-existing behavior.
  const handleFilterChanged = useCallback(() => {
    if (enabledRef.current && isAllSelectedRef.current) {
      clearSelectionModel();
    }
  }, [clearSelectionModel]);

  const getDownloadMeta = useCallback(
    () => ({
      isAllSelected: isAllSelectedRef.current,
      excludeIds: Array.from(excludeIdsRef.current.values())
    }),
    []
  );

  // Auto-check newly fetched pages (minus exclusions) while armed.
  useEffect(() => {
    if (!enabledRef.current || records.length === 0) return;

    const pageIds = records.map(getRecordId).filter(Boolean);
    if (pageIds.length === 0) {
      // Index-based row ids collide across pages and break select-all.
      logger.warn(
        'usePeacockSelection: records have no id/uuid keys — dataset-wide select-all cannot work reliably.'
      );
      return;
    }

    if (!isAllSelectedRef.current || !tableRef.current) return;

    const wanted = pageIds.filter((id) => !excludeIdsRef.current.has(id));
    if (wanted.length === 0) return;
    // Everything already reported — skip the write.
    if (wanted.every((id) => prevReportedIdsRef.current.has(id))) return;

    expectedReportRef.current = new Set([
      ...prevReportedIdsRef.current,
      ...wanted
    ]);
    tableRef.current.setRowSelection((prev) => {
      const next = {...prev};
      records.forEach((record) => {
        const id = getRecordId(record);
        if (id && !excludeIdsRef.current.has(id)) {
          next[id] = true;
        }
      });
      return next;
    });
  }, [records]);

  return {
    selectionUi,
    selectedRowsRef,
    tableRef,
    handleTableReady,
    handleRowSelectionUpdate,
    handleFilterChanged,
    enterSelectAllMode,
    clearSelectionModel,
    getDownloadMeta
  };
};
